/*
  ZEVQILO Atelier — wishlist.js
  Device-level wishlist stored in localStorage (no account or app required).
  <wishlist-button> toggles a product; <wishlist-grid> renders saved products on the wishlist page;
  [data-wishlist-count] shows the count in the header.
*/
(() => {
  const theme = window.theme;
  const KEY = 'zq:wishlist:v1';
  const MAX_ITEMS = 60;

  const read = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((h) => typeof h === 'string') : [];
    } catch (error) {
      return [];
    }
  };

  const write = (handles) => {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(handles.slice(0, MAX_ITEMS)));
    } catch (error) {
      /* Storage full or blocked (private mode): keep the in-memory state for this page. */
    }
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handles } }));
  };

  const wishlist = {
    all: read,
    has: (handle) => read().includes(handle),
    toggle(handle) {
      const handles = read();
      const index = handles.indexOf(handle);
      if (index > -1) {
        handles.splice(index, 1);
      } else {
        handles.unshift(handle);
      }
      write(handles);
      return index === -1;
    },
    remove(handle) {
      write(read().filter((h) => h !== handle));
    },
  };
  theme.wishlist = wishlist;

  const updateCounts = () => {
    const count = read().length;
    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = count === 0;
    });
  };

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.handle = this.dataset.handle;
      if (!this.button || !this.handle) return;
      this.onClick = this.onClick.bind(this);
      this.sync = this.sync.bind(this);
      this.button.addEventListener('click', this.onClick);
      document.addEventListener('wishlist:change', this.sync);
      this.sync();
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.onClick);
      document.removeEventListener('wishlist:change', this.sync);
    }

    onClick(event) {
      event.preventDefault();
      event.stopPropagation();
      const added = wishlist.toggle(this.handle);
      theme.announce(added ? theme.strings.wishlistAdded : theme.strings.wishlistRemoved);
    }

    sync() {
      const saved = wishlist.has(this.handle);
      this.button.setAttribute('aria-pressed', String(saved));
      this.button.setAttribute('aria-label', saved ? this.button.dataset.labelRemove : this.button.dataset.labelAdd);
    }
  }
  customElements.define('wishlist-button', WishlistButton);

  class WishlistGrid extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-items]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.loading = this.querySelector('[data-wishlist-loading]');
      this.onChange = theme.debounce(() => this.render(), 50);
      document.addEventListener('wishlist:change', this.onChange);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('wishlist:change', this.onChange);
    }

    async render() {
      const handles = read();
      const rendered = new Set(Array.from(this.grid.children).map((el) => el.dataset.handle));

      Array.from(this.grid.children).forEach((el) => {
        if (!handles.includes(el.dataset.handle)) el.remove();
      });

      const missing = handles.filter((h) => !rendered.has(h));
      if (missing.length && this.loading) this.loading.hidden = false;

      const results = await Promise.all(
        missing.map(async (handle) => {
          try {
            const url = theme.sectionUrl(`${theme.routes.root}products/${encodeURIComponent(handle)}`, 'product-card-render');
            const response = await fetch(url);
            if (response.status === 404) {
              wishlist.remove(handle);
              return null;
            }
            if (!response.ok) return null;
            const doc = theme.parseHTML(await response.text());
            const card = doc.querySelector('[data-product-card]');
            if (!card) return null;
            const item = document.createElement('li');
            item.dataset.handle = handle;
            item.append(card);
            return item;
          } catch (error) {
            return null;
          }
        })
      );

      const current = read();
      results
        .filter(Boolean)
        .forEach((item) => {
          if (current.includes(item.dataset.handle)) this.grid.append(item);
        });

      const order = read();
      Array.from(this.grid.children)
        .sort((a, b) => order.indexOf(a.dataset.handle) - order.indexOf(b.dataset.handle))
        .forEach((el) => this.grid.append(el));

      if (this.loading) this.loading.hidden = true;
      if (this.empty) this.empty.hidden = this.grid.children.length > 0;
    }
  }
  customElements.define('wishlist-grid', WishlistGrid);

  document.addEventListener('wishlist:change', updateCounts);
  window.addEventListener('storage', (event) => {
    if (event.key === KEY) document.dispatchEvent(new CustomEvent('wishlist:change'));
  });
  document.addEventListener('DOMContentLoaded', updateCounts);
})();
